import type { SimEvent } from '../core/events';
import { isMatter, isTuple } from '../core/packet';
import type { MaterialTuple } from '../core/packet';
import { peekPort, portHasRoom, singleOutput, tryPushToPort, tryTakeFromPort } from '../core/port';
import type { PacketId } from '../core/types';
import { drainOutput } from './shared';
import type { MachineHandler } from './registry';

/**
 * Takes the head of every input port at once, or nothing at all. Waiting on a partner is `zip`'s
 * defined behaviour rather than a handler failing to cope, so the packets it parks are neither
 * dropped nor errored — and because its ports are capacity-1 like every other machine's, the queue
 * that forms is a visible line of packets backed up the conveyor, not a hidden buffer.
 *
 * It transforms nothing: the heads it takes leave as one tuple packet carrying both materials, in
 * input-port order, and it is `map` that turns a tuple into a product. Nothing it can be handed is
 * an error, since it never looks at what it is packing.
 *
 * Zipping a tuple concatenates rather than nesting — the game's tuple is flat, so chaining two joins
 * builds the three-wide tuple a three-input recipe will want, not RxJS's `[[a, b], c]`.
 *
 * Cash is the one thing it will not pack, since a tuple is a pair of materials and proceeds are
 * neither. Such a packet is held at its input the way `map` holds one no recipe consumes.
 */
export const zipHandler: MachineHandler = {
  kind: 'zip',
  step({ state, machine }) {
    if (machine.kind !== 'zip') return [];
    const events: SimEvent[] = [];
    const outputPort = singleOutput(machine.outputs);

    drainOutput(state, machine, events);
    if (!portHasRoom(outputPort)) return events;

    const waiting = machine.inputs.map(peekPort);
    if (waiting.some((packetId) => packetId === undefined)) return events;

    const ids = waiting as PacketId[];
    const packets = ids.map((packetId) => state.packets[packetId]).filter((packet) => !!packet);
    if (packets.length < ids.length) {
      machine.inputs.forEach((port) => tryTakeFromPort(port));
      return events;
    }

    const materials = packets.map((packet) => packet.material);
    if (!materials.every(isMatter)) return events;

    machine.inputs.forEach((port) => tryTakeFromPort(port));
    const tuple: MaterialTuple = materials.flatMap((material) =>
      isTuple(material) ? [...material] : [material],
    );
    const [survivor, ...consumed] = packets;
    survivor.material = tuple;

    events.push({ type: 'packetTransformed', packetId: survivor.id, material: tuple });
    for (const packet of consumed) {
      delete state.packets[packet.id];
      events.push({ type: 'packetDespawned', packetId: packet.id });
    }
    tryPushToPort(outputPort, survivor.id);
    return events;
  },
};
